import { useEffect, useState } from "react";
import { CalendarDays, Loader2, Mail, MessageSquare, Plus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { toast } from "sonner";
import { estApi, preconApi } from "../lib/api";
import { requestNewCustomer } from "../lib/navigation";
import { EmailsTab, NotesPanel } from "./BidCardTabs";
import { clearMentions, unreadFor, useMentions } from "../lib/mentions";
import PersonChip from "./PersonChip";

const stop = (e) => e.stopPropagation();

/** EST / AM avatar with a hover card (full name + active bids) and an invisible select to reassign. */
export function PersonCell({ job, which, people, activeCount, canEdit, onAssign }) {
  const isEst = which === "estimator";
  const email = isEst ? job.estimator_email : job.account_manager_email;
  const name = isEst ? job.estimator_name : job.account_manager_name;
  const avatar = email ? <PersonChip name={name || email} testid={`board-${which}-avatar-${job.id}`} />
    : <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-dashed border-red-400 text-[10px] text-red-600" data-testid={`board-${which}-avatar-${job.id}`}>?</span>;
  return (
    <HoverCard openDelay={150} closeDelay={50}>
      <HoverCardTrigger asChild>
        <label className={`relative inline-flex ${canEdit ? "cursor-pointer" : ""}`} onClick={stop}>
          {avatar}
          {canEdit && (
            <select value={email || ""} onChange={(e) => onAssign(e.target.value)} data-testid={`board-${which}-${job.id}`}
                    className="absolute inset-0 cursor-pointer opacity-0">
              <option value="">unassigned</option>
              {people.map((p) => <option key={p.email} value={p.email}>{p.name || p.email}</option>)}
              {email && !people.some((p) => p.email === email) && <option value={email}>{email}</option>}
            </select>)}
        </label>
      </HoverCardTrigger>
      <HoverCardContent side="top" align="start" className="w-auto px-3 py-2 text-xs" data-testid={`board-${which}-hover-${job.id}`}>
        <div className="font-semibold text-foreground">{name || email || "Unassigned"}</div>
        {email && <div className="text-muted-foreground">{activeCount} active bid{activeCount === 1 ? "" : "s"}</div>}
        {!email && <div className="text-muted-foreground">{canEdit ? "Click to assign" : "Nobody assigned yet"}</div>}
      </HoverCardContent>
    </HoverCard>
  );
}

/** Customer(s) cell — same look as the Estimate grid: one plain name, or "Multiple GC (n)". */
export function CustomersCell({ job, canEdit, onChanged }) {
  const inv = job.invitations || [];
  const names = inv.map((i) => i.gc_name).filter(Boolean);
  return (
    <span className="inline-flex items-center gap-1.5">
      {names.length > 1 ? (
        <span className="cursor-help underline decoration-dotted decoration-slate-300" title={names.join("\n")} data-testid={`board-gc-multi-${job.id}`}>
          Multiple GC ({names.length})</span>
      ) : names.length === 1 ? (
        <span className={inv[0]?.status === "Declined" ? "line-through opacity-50" : ""} data-testid={`board-gc-${inv[0].id}`}>{names[0]}</span>
      ) : <span className="text-muted-foreground">—</span>}
      {canEdit && <AddCustomer job={job} onChanged={onChanged} />}
    </span>
  );
}

/** Inline notes / emails pop-overs on a board row — no full job drawer. */
export function RowNotesButton({ job, onChanged }) {
  const { items } = useMentions();
  const lit = unreadFor(items, { job_id: job.id }) > 0;
  return (
    <Popover onOpenChange={(o) => o && clearMentions({ job_id: job.id })}>
      <PopoverTrigger asChild>
        <button type="button" onClick={stop} data-testid={`row-notes-${job.id}`} title={lit ? "You were mentioned — open to read" : "Notes"} data-mention={lit ? "1" : "0"}
                className={`inline-flex items-center gap-0.5 text-[10px] ${lit ? "mention-lit" : "text-muted-foreground hover:text-foreground"}`}>
          <MessageSquare className="h-3.5 w-3.5" />{(job.notes || []).length || ""}</button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[26rem] max-h-[28rem] overflow-y-auto p-3" onClick={stop} data-testid={`row-notes-pop-${job.id}`}>
        <div className="pb-2 text-[11px] font-semibold text-muted-foreground">Notes · {job.project_name}</div>
        <NotesPanel job={job} onChanged={onChanged} />
      </PopoverContent>
    </Popover>
  );
}

export function RowEmailsButton({ job }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" onClick={stop} data-testid={`row-emails-${job.id}`} title="Emails"
                className="inline-flex items-center text-[10px] text-muted-foreground hover:text-foreground"><Mail className="h-3.5 w-3.5" /></button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[32rem] max-h-[28rem] overflow-y-auto p-3" onClick={stop} data-testid={`row-emails-pop-${job.id}`}>
        <div className="pb-1 text-[11px] font-semibold text-muted-foreground">Emails · {job.project_name}</div>
        <EmailsTab jobId={job.id} />
      </PopoverContent>
    </Popover>
  );
}

const DAY = ["Su", "M", "T", "W", "Th", "F", "Sa"];
/** Bid due: "Th 04/27/2026" + a tight calendar icon (native date picker). */
export function DueDateCell({ value, testid, className = "", onSave }) {
  const v = (value || "").slice(0, 10);
  const d = v ? new Date(`${v}T00:00:00`) : null;
  const open = (e) => { stop(e); try { e.currentTarget.parentElement.querySelector("input")?.showPicker(); } catch { /* older browsers: input is clickable */ } };
  return (
    <span className={`relative inline-flex items-center gap-1 text-[11px] ${className}`} onClick={stop}>
      {d && <span data-testid={`${testid}-text`}>{`${DAY[d.getDay()]} ${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`}</span>}
      <button type="button" onClick={open} title="Pick a date" data-testid={`${testid}-pick`} className="text-muted-foreground hover:text-[#007BFF]"><CalendarDays className={d ? "h-3 w-3" : "h-3.5 w-3.5"} /></button>
      <input type="date" value={v} onChange={(e) => onSave(e.target.value || null)} data-testid={testid} tabIndex={-1}
             className="pointer-events-none absolute bottom-0 right-0 h-0 w-0 opacity-0" />
    </span>
  );
}

/** Editable date cell; saves on change. */
export function DateCell({ value, testid, className = "", onSave, disabled }) {
  return (
    <input type="date" value={(value || "").slice(0, 10)} disabled={disabled} onClick={stop} data-testid={testid}
           onChange={(e) => onSave(e.target.value || null)}
           className={`h-6 w-[7.5rem] rounded border border-transparent bg-transparent px-1 text-[11px] hover:border-border focus:border-border focus:outline-none disabled:cursor-default dark:[color-scheme:dark] ${className}`} />
  );
}

/** "+" next to the customer names — pick a GC from the shared directory, or go add a new one. */
export function AddCustomer({ job, onChanged, onPick, already: alreadyNames, testid, children, companyType = "gc", onNew }) {
  const [open, setOpen] = useState(false);
  const [companies, setCompanies] = useState(null);
  useEffect(() => {
    if (!open || companies) return;
    estApi.getCompanies({ company_type: companyType, limit: 500 }).then((r) => setCompanies(r.companies || [])).catch(() => setCompanies([]));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  // Optional overrides let other grids (Basis of Design) reuse this picker for their own GC field.
  const already = new Set((alreadyNames || (job.invitations || []).map((i) => i.gc_name)).map((n) => (n || "").toLowerCase()));
  const tid = testid || `board-add-gc-${job.id}`;
  const pick = async (name) => {
    try {
      if (onPick) await onPick(name); else await preconApi.addInvitation(job.id, { gc_name: name });
      toast.success(`${name} added`); setOpen(false); onChanged?.();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add the customer"); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children || (
        <button type="button" onClick={stop} title="Add customer" data-testid={tid}
                className="inline-flex h-5 w-5 items-center justify-center rounded border border-dashed border-border text-muted-foreground hover:text-foreground"><Plus className="h-3 w-3" /></button>)}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0" onClick={stop} data-testid={`${tid}-pop`}>
        <Command>
          <CommandInput placeholder="Search..." className="h-8 text-xs" data-testid={`${tid}-search`} />
          <CommandList className="max-h-56">
            {!companies ? <div className="flex items-center justify-center py-4 text-xs text-muted-foreground"><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Loading…</div>
              : <CommandEmpty className="py-3 text-center text-xs text-muted-foreground">No {companyType === "gc" ? "general contractor" : companyType} matches.</CommandEmpty>}
            <CommandGroup>
              {(companies || []).map((c) => (
                <CommandItem key={c.id} value={c.name} disabled={already.has(c.name.toLowerCase())} onSelect={() => pick(c.name)}
                             className="text-xs" data-testid={`${tid}-option-${c.id}`}>
                  <span className="truncate">{c.name}</span>
                  {[c.city, c.state].filter(Boolean).length > 0 && <span className="ml-auto pl-2 text-[10px] text-muted-foreground">{[c.city, c.state].filter(Boolean).join(", ")}</span>}
                </CommandItem>))}
            </CommandGroup>
          </CommandList>
          <div className="border-t border-border p-1">
            <button type="button" onClick={() => { setOpen(false); (onNew || requestNewCustomer)(job); }} data-testid={`${tid}-new`}
                    className="flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-xs text-[#007BFF] hover:bg-muted">
              <Plus className="h-3 w-3" /> Add new {companyType === "gc" ? "customer" : companyType} in Settings › Directory
            </button>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
